import {Button, Icon, IconButton, TextField} from '@wandb/components';
import type {AgentQuestion} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import React, {useState} from 'react';

const OTHER_PLACEHOLDER = 'Type your own answer…';

export interface WBAgentQuestionCarouselProps {
  questions: AgentQuestion[];
  /** Submit the assembled answers as the next prompt. */
  onSubmit: (prompt: string) => void;
  /** Escape hatch — abandon the questions and type a free-form message. */
  onTypeInstead: () => void;
  /** Whether submission is disabled (e.g. a run is in progress). */
  isDisabled?: boolean;
}

/**
 * WBAgentQuestionCarousel presents the questions an agent attached to its last
 * turn as a guided, single-answer-per-step dialog.
 * Each step offers the question's choices plus a free-form "other" entry;
 * answering the last question advances to a review step that assembles the
 * answers into a plain-text next prompt.
 *
 * The next turn is free to ignore the questions, so an escape hatch (the "x")
 * lets the user dismiss the carousel and type a normal message instead.
 */
export const WBAgentQuestionCarousel = ({
  questions,
  onSubmit,
  onTypeInstead,
  isDisabled = false,
}: WBAgentQuestionCarouselProps) => {
  // step in [0, questions.length] — the final index is the review step.
  // Answers are keyed by question index (questions have no id).
  const [step, setStep] = useState(0);
  const [choice, setChoice] = useState<Record<number, string>>({});
  const [other, setOther] = useState<Record<number, string>>({});

  const answerFor = (index: number): string => {
    const free = other[index]?.trim();
    return free ? free : (choice[index] ?? '');
  };

  const isReviewStep = step >= questions.length;
  const current = isReviewStep ? null : questions[step];
  const currentAnswered = !isReviewStep && answerFor(step) !== '';

  // Advance one step — to the next question, or to the review step after the
  // last question. Questions may be skipped (an empty answer is allowed), so
  // advancing never requires the current question to be answered.
  const advance = () => setStep(s => Math.min(questions.length, s + 1));

  const selectOption = (index: number, option: string) => {
    setChoice(prev => ({...prev, [index]: option}));
    setOther(prev => ({...prev, [index]: ''}));
    advance();
  };

  const changeOther = (index: number, value: string) => {
    setOther(prev => ({...prev, [index]: value}));
    if (value.trim() !== '') {
      setChoice(prev => {
        const next = {...prev};
        delete next[index];
        return next;
      });
    }
  };

  const handleSubmit = () => {
    if (isDisabled) {
      return;
    }
    onSubmit(formatAnswers(questions, answerFor));
  };

  return (
    <div className="flex flex-col gap-12 rounded border border-moon-250 bg-white p-12">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <IconButton
            size="sm"
            variant="ghost"
            icon="chevron-back"
            aria-label="previous question"
            isDisabled={step === 0}
            onClick={() => setStep(s => Math.max(0, s - 1))}
          />
          <span className="text-sm font-semibold text-moon-800">
            {isReviewStep
              ? 'Review your answers'
              : `Question ${step + 1} of ${questions.length}`}
          </span>
          <IconButton
            size="sm"
            variant="ghost"
            icon="chevron-next"
            aria-label="next question"
            isDisabled={isReviewStep}
            onClick={advance}
          />
        </div>
        <IconButton
          size="sm"
          variant="ghost"
          icon="close"
          aria-label="dismiss questions"
          onClick={onTypeInstead}
        />
      </div>

      {current != null ? (
        <QuestionStep
          question={current}
          selectedOption={choice[step]}
          otherValue={other[step] ?? ''}
          onSelectOption={option => selectOption(step, option)}
          onChangeOther={value => changeOther(step, value)}
          onSubmitOther={() => {
            if (currentAnswered) {
              advance();
            }
          }}
        />
      ) : (
        <>
          <AnswerSummary questions={questions} answerFor={answerFor} />
          <div className="flex justify-end">
            <Button
              variant="primary"
              isDisabled={isDisabled}
              onPress={handleSubmit}>
              Send answers
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const QuestionStep = ({
  question,
  selectedOption,
  otherValue,
  onSelectOption,
  onChangeOther,
  onSubmitOther,
}: {
  question: AgentQuestion;
  selectedOption: string | undefined;
  otherValue: string;
  onSelectOption: (option: string) => void;
  onChangeOther: (value: string) => void;
  onSubmitOther: () => void;
}) => {
  return (
    <div className="flex flex-col gap-8">
      <span className="text-base text-moon-800">{question.question}</span>
      {/* One option per row. Each option wraps to multiple lines and, past a
          max height, scrolls — so long answers never overflow the container
          (the design-system ToggleButton can't wrap its label, so we render a
          custom selectable row). */}
      <div className="flex w-full flex-col gap-8">
        {question.options.map(option => {
          const isSelected = selectedOption === option;
          return (
            <button
              key={option}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelectOption(option)}
              className={`flex max-h-[140px] w-full overflow-y-auto whitespace-normal break-words rounded-md border px-12 py-8 text-left text-sm transition-colors ${
                isSelected
                  ? 'border-teal-450 bg-teal-300/[0.48] text-teal-600'
                  : 'border-moon-250 bg-white text-moon-800 hover:bg-moon-100'
              }`}>
              {option}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-8">
        <div className="flex-1">
          <TextField
            aria-label="custom answer"
            fullWidth={true}
            placeholder={OTHER_PLACEHOLDER}
            value={otherValue}
            onChange={onChangeOther}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSubmitOther();
              }
            }}
          />
        </div>
        <Button
          variant="secondary"
          isDisabled={otherValue.trim() === ''}
          onPress={onSubmitOther}>
          Submit
        </Button>
      </div>
    </div>
  );
};

const AnswerSummary = ({
  questions,
  answerFor,
}: {
  questions: AgentQuestion[];
  answerFor: (index: number) => string;
}) => {
  return (
    <div className="flex flex-col gap-8">
      {questions.map((question, index) => {
        const answer = answerFor(index);
        return (
          // oxlint-disable-next-line react/no-array-index-key
          <div key={index} className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-moon-800">
              {question.question}
            </span>
            {answer ? (
              <span className="flex items-center gap-4 text-sm text-moon-600">
                <Icon icon="checkmark" color="teal" />
                {answer}
              </span>
            ) : (
              <span className="text-sm italic text-moon-400">No answer</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * formatAnswers renders the collected answers as a plain-text prompt the agent
 * reads as normal user input. The next turn may ignore them entirely.
 */
export function formatAnswers(
  questions: AgentQuestion[],
  answerFor: (index: number) => string
): string {
  return questions
    .map((q, i) => {
      const answer = answerFor(i);
      return `Q${i + 1}: ${q.question}\nA${i + 1}: ${answer || '(no answer)'}`;
    })
    .join('\n\n');
}
